import React, { useEffect, useContext, useState } from "react";
import "./MapComponent.scss";
import AppContext from "../Contexts/AppContext";
import {
  add_visualization_layers_to_map,
  get_initial_map,
  recenterOnUser,
  showUserLocation,
} from "../../services/MapCore/MapService";
import { addMarkerToMap } from "../../services/MapCore/MarkerService";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ContextMenuWrapper from "../CustomComponents/ContextMenu/ContextMenuWrapper";

function MapComponent(props) {
  const { t } = useTranslation("main");
  const {
    setSelectedFeatures,
    layersGroups,
    map,
    setMap,
    mapElementRef,
    mapInstanceRef,
    setIsWindowFeatureDetailsOpen,
    layersGroupsRef,
    setLoadingClick,
    clickCoordinates,
    setClickCoordinates,
    modeOptions,
    setModeOptions,
    setEditedGeometry,
    drawerWidth,
    openDrawer,
    geoStatus,
    setGeoStatus,
  } = useContext(AppContext);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [contextMenuActions, setContextMenuActions] = useState([]);
  const isMobile = window.innerWidth < 960;
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    let longPressTimer = null;
    let isLongPress = false;
    let isDragging = false; // Nueva variable para detectar movimiento
    let startX = 0;
    let startY = 0;
    const DRAG_THRESHOLD = 10; // px
    let touchStartTime = 0;

    const leftClickCallback = async function (event) {
      // console.log("leftClickCallback", event);
      if (modeOptions.ADD_POLYGON.length > 0) return;
      if (modeOptions.CARBON_SINK.length > 0) return;
      if (event.preventDefault) event.preventDefault();
      if (isLongPress || isDragging) return; // ❌ Evita single click si hubo long press o el usuario arrastró el mapa

      // const coordinates = event.coordinate;

      // const view = mapInstanceRef.current.getView();
      const mapSize = mapInstanceRef.current.getSize(); // [ancho, alto] del mapa
      const mapWidth = mapSize ? mapSize[0] : window.innerWidth; // Ancho del mapa
      const panelWidth = Math.min(640, mapWidth * 0.9); // 40rem = 640px, máx 90vw
      const drawerOffset = openDrawer ? drawerWidth : 0; // Si el drawer está abierto, tiene un ancho
      // Ancho disponible en la izquierda
      const availableSpace = mapWidth - (drawerOffset + panelWidth);

      // Si el espacio disponible es menor a 200px, centramos en pantalla
      let newCenterPixel;
      if (availableSpace < 100) {
        newCenterPixel = [mapWidth / 2, event.pixel[1]]; // Centrado en la pantalla
      } else {
        const moveLeft = panelWidth / 2 - drawerOffset / 2;
        newCenterPixel = [event.pixel[0] + moveLeft, event.pixel[1]];
      }

      // Centrar el mapa donde se hace click
      // const newCenterCoordinate =
      //   mapInstanceRef.current.getCoordinateFromPixel(newCenterPixel);

      // mapInstanceRef.current.getView().animate({
      //   // center: coordinates,
      //   center: newCenterCoordinate,
      //   duration: 500, // Animación de medio segundo
      // });

      addMarkerToMap(mapInstanceRef.current, event.coordinate);
      setLoadingClick(true);
      setClickCoordinates(event.coordinate);
      setSelectedFeatures([]);

      let toast_id;
      if (modeOptions.ASBESTOS_CLICK.length === 0) {
        toast_id = toast.loading(`${t("obtaining_data")}...`, {
          position: "top-right",
        });
      }

      const views = [];
      for (const layerGroup of layersGroupsRef.current) {
        const view = await layerGroup.onLeftClick({
          event,
          map: mapInstanceRef.current,
          modeOptions,
          setEditedGeometry,
        });
        if (view) {
          views.push({ name: layerGroup.name, view: view, id: layerGroup.id });
        }
      }

      if (toast_id) toast.dismiss(toast_id);
      setLoadingClick(false);
      setSelectedFeatures(views);
      setIsWindowFeatureDetailsOpen(views.length > 0);

      if (views.length === 0 && modeOptions.ASBESTOS_CLICK.length === 0) {
        toast.error(t("no_info_to_display"), { position: "top-right" });
      }
      if (event.stopPropagation) event.stopPropagation();
    };

    const handleLongPressStart = (event) => {
      if (event.touches.length > 1) return;

      isLongPress = false;
      isDragging = false;
      touchStartTime = new Date().getTime();

      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;

      longPressTimer = setTimeout(() => {
        if (!isDragging) {
          isLongPress = true;
          const pixel = map.getEventPixel(touch);
          const coordinate = map.getCoordinateFromPixel(pixel);

          setClickCoordinates(coordinate);
          leftClickCallback({ coordinate, pixel });
        }
      }, 500);
    };

    const handleTouchMove = (event) => {
      if (event.touches.length > 1) {
        isDragging = true;
        clearTimeout(longPressTimer);
        return;
      }

      const touch = event.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > DRAG_THRESHOLD) {
        isDragging = true;
        clearTimeout(longPressTimer);
      }
    };

    const handleTouchEnd = (event) => {
      const touchDuration = new Date().getTime() - touchStartTime;
      clearTimeout(longPressTimer);

      if (!isLongPress && !isDragging && touchDuration < 500) {
        // ✅ Si no hubo long press ni arrastre, ejecutamos single click
        const touch = event.changedTouches[0];
        const pixel = map.getEventPixel(touch);
        const coordinate = map.getCoordinateFromPixel(pixel);

        leftClickCallback({ coordinate, pixel });
      }
    };

    if (!isMobile) {
      map.on("singleclick", leftClickCallback);
    } else {
      map.getViewport().addEventListener("touchstart", handleLongPressStart);
      map.getViewport().addEventListener("touchmove", handleTouchMove);
      map.getViewport().addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      if (!isMobile) {
        map.un("singleclick", leftClickCallback);
      } else {
        map
          .getViewport()
          .removeEventListener("touchstart", handleLongPressStart);
        map.getViewport().removeEventListener("touchmove", handleTouchMove);
        map.getViewport().removeEventListener("touchend", handleTouchEnd);
      }
    };
  }, [mapInstanceRef.current, modeOptions, openDrawer, drawerWidth]);

  // RIGHT CLICK O LONG PRESS
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    const DRAG_THRESHOLD = 10; // px

    const map = mapInstanceRef.current;
    if (!map) return;

    let longPressTimer = null;

    const handleContextMenu = async (event) => {
      // console.log("eventtt", event);
      if (event.preventDefault) event.preventDefault(); // Evita el menú contextual del navegador
      const coordinates = event.coordinate;
      setClickCoordinates(coordinates);

      const actions = [];
      for (const layerGroup of layersGroupsRef.current) {
        if (typeof layerGroup.onRightClick === "function") {
          const newActions = await layerGroup.onRightClick({
            event,
            map,
            setEditedGeometry,
            modeOptions,
            setModeOptions,
          });

          if (newActions) {
            actions.push(...newActions);
          }
        }
      }

      setContextMenuActions(actions);
      setMenuAnchor({ mouseX: event.pixel[0], mouseY: event.pixel[1] });
    };

    const handleLongPressStart = (event) => {
      if (event.touches.length > 1) return;

      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;

      longPressTimer = setTimeout(() => {
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= DRAG_THRESHOLD) {
          const pixel = map.getEventPixel(touch);
          const coordinate = map.getCoordinateFromPixel(pixel);
          setClickCoordinates(coordinate);
          handleContextMenu({ coordinate, pixel });
        }
      }, 500);
    };

    const cancelLongPress = (event) => {
      if (event.touches && event.touches.length === 1) {
        const touch = event.touches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // if (distance > DRAG_THRESHOLD) {
        //   console.log("touchh right cancelLongPress: movement detected");
        // }
      }

      clearTimeout(longPressTimer);
    };

    if (!isMobile) {
      map.on("contextmenu", handleContextMenu);
    } else {
      map.getViewport().addEventListener("touchstart", handleLongPressStart, {
        // passive: false,
      });
      map.getViewport().addEventListener("touchend", cancelLongPress, {
        // passive: false,
      });
      map.getViewport().addEventListener("touchmove", cancelLongPress, {
        // passive: false,
      });
    }

    // Ocultar menú si se hace clic en cualquier parte del mapa
    const handleClickOutside = () => {
      setMenuAnchor(null);
      setContextMenuActions([]);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuAnchor(null);
        setContextMenuActions([]);
      }
    };

    if (!isMobile) document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown); // Escape para cerrar menú

    return () => {
      if (!isMobile) {
        map.un("contextmenu", handleContextMenu);
      } else {
        map
          .getViewport()
          .removeEventListener("touchstart", handleLongPressStart);
        map.getViewport().removeEventListener("touchend", cancelLongPress);
        map.getViewport().removeEventListener("touchmove", cancelLongPress);
      }

      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mapInstanceRef.current, modeOptions]);

  useEffect(() => {
    const map_init = () => {
      const initialMap = get_initial_map(mapElementRef);
      setMap(initialMap);
      mapInstanceRef.current = initialMap;
      return initialMap;
    };

    // Crear el mapa base
    let initialMap = null;
    if (mapElementRef && !mapInstanceRef.current && map == null) {
      initialMap = map_init();
    } else {
      console.error("MapRef no existe??", map, mapElementRef);
    }
    return () => {
      if (initialMap) {
        initialMap.setTarget(null); // Limpiar el mapa al desmontar
        if (map) {
          setMap(null);
        }
      }
      if (mapElementRef.current) {
        mapElementRef.current.setTarget(null);
        mapElementRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(null);
        mapInstanceRef.current = null;
      }
    };
  }, [mapElementRef, mapInstanceRef]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      const baseLayer = mapInstanceRef.current.getLayers().item(0);

      const layersToKeep = mapInstanceRef.current
        .getLayers()
        .getArray()
        .filter((l) =>
          ["userLocationLayer", "markerLayer", "highlightLayer"].includes(
            l.get("name")
          )
        );

      mapInstanceRef.current.getLayers().clear();
      mapInstanceRef.current.getLayers().push(baseLayer);

      add_visualization_layers_to_map(
        mapInstanceRef.current,
        layersGroupsRef.current
      );
      layersToKeep.forEach((l) => {
        if (!map.getLayers().getArray().includes(l)) map.addLayer(l);
      });
    } else {
      console.error(
        "ERROR - Mapa no existente, error renderizando campas.",
        mapInstanceRef
      );
    }
  }, [layersGroups]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const handle = showUserLocation(
      map,
      { follow: true, showAccuracy: true, firstFixTimeout: 8000 },
      (evt) => {
        switch (evt.type) {
          case "requesting":
            setGeoStatus("requesting");
            break;
          case "ready":
            setGeoStatus("ready");
            break;
          case "update":
            setGeoStatus("updating");
            break;
          case "error":
            setGeoStatus("error");
            toast.error(t("location_unavailable") || "Location unavailable");
            break;
          default:
            break;
        }
      }
    );

    return () => handle.stop();
  }, [mapInstanceRef.current]);

  return (
    <div
      {...props}
      ref={mapElementRef}
      className="map-container"
      style={{ cursor: modeOptions.ADD_POLYGON.length > 0 ? "none" : "" }}
    >
      <div
        style={{
          height: "100vh",
          height: "100dvh",
          width: "100vw",
          width: "100dvw",
          backgroundColor: "rgba(255,0,0,0.1)",
          display: "none",
        }}
      />

      <ContextMenuWrapper
        key={menuAnchor?.mouseX}
        anchorPosition={menuAnchor}
        actions={[
          ...contextMenuActions,
          {
            label: "link_to",
            items: [
              {
                action: () => {
                  window.open(
                    `https://www.google.com/maps?q=${clickCoordinates[1]},${clickCoordinates[0]}`
                  );
                },
                label: "Google Maps",
              },
              {
                action: () => {
                  window.open(
                    `https://earth.google.com/web/@${clickCoordinates[1]},${clickCoordinates[0]},5000a`
                  );
                },
                label: "Google Earth",
              },
            ],
          },
        ]}
        onClose={() => setMenuAnchor(null)}
      />
    </div>
  );
}

export default MapComponent;
